import { createFileRoute, Link } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { Loader2 } from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuth } from "@/hooks/use-auth";
import { loginWithSSO } from "@/lib/api/auth";
import { ApiError } from "@/lib/api-client";
import { setAuthFlash } from "@/lib/auth-flash";
import { clearTokens } from "@/lib/auth-storage";
import { APP_NAME } from "@/lib/constants";

const searchSchema = z.object({
  access_token: z.string().optional(),
  // Nexus may attach this (same as Pentools); ignored for same-origin deploys.
  api_base_url: z.string().optional(),
});

export const Route = createFileRoute("/sso-callback")({
  validateSearch: (search) => searchSchema.parse(search),
  component: SSOCallbackPage,
});

/** Read the Nexus token from the landing URL before any router/search stripping. */
function readNexusTokenFromLocation(): string {
  if (typeof window === "undefined") return "";
  const fromQuery = new URLSearchParams(window.location.search).get("access_token");
  if (fromQuery?.trim()) return fromQuery.trim();

  const hash = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : window.location.hash;
  if (hash) {
    const fromHash = new URLSearchParams(hash).get("access_token");
    if (fromHash?.trim()) return fromHash.trim();
  }
  return "";
}

function stripTokenFromUrl() {
  const url = new URL(window.location.href);
  url.searchParams.delete("access_token");
  url.searchParams.delete("api_base_url");
  if (url.hash) {
    const hashParams = new URLSearchParams(
      url.hash.startsWith("#") ? url.hash.slice(1) : url.hash,
    );
    let changed = false;
    for (const key of ["access_token", "api_base_url"] as const) {
      if (hashParams.has(key)) {
        hashParams.delete(key);
        changed = true;
      }
    }
    if (changed) {
      const next = hashParams.toString();
      url.hash = next ? `#${next}` : "";
    }
  }
  window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
}

type CallbackState =
  | { kind: "loading" }
  | { kind: "error"; message: string };

function SSOCallbackPage() {
  const { access_token: searchToken } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { login } = useAuth();
  const queryClient = useQueryClient();
  const startedRef = useRef(false);
  const [capturedToken] = useState(
    () => searchToken?.trim() || readNexusTokenFromLocation(),
  );
  const [state, setState] = useState<CallbackState>(() =>
    capturedToken
      ? { kind: "loading" }
      : {
          kind: "error",
          message: "Missing SSO access token. Sign in from Nexus and try again.",
        },
  );

  useEffect(() => {
    if (!capturedToken || startedRef.current) return;
    startedRef.current = true;

    void (async () => {
      clearTokens();
      await queryClient.cancelQueries({ queryKey: ["auth", "me"] });
      queryClient.removeQueries({ queryKey: ["auth", "me"] });
      stripTokenFromUrl();
      try {
        const response = await loginWithSSO(capturedToken);
        if (!response.access_token?.trim()) {
          throw new Error("SSO login returned an empty access token");
        }
        login(response.access_token, response.refresh_token, response.user);
        await navigate({ to: "/", replace: true });
      } catch (err) {
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "SSO login failed";
        clearTokens();
        setAuthFlash(message);
        setState({ kind: "error", message });
      }
    })();
  }, [capturedToken, login, navigate, queryClient]);

  if (state.kind === "error") {
    return (
      <div className="flex min-h-svh items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>SSO sign-in failed</CardTitle>
            <CardDescription>{state.message}</CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Link
              to="/login"
              className="inline-flex h-8 items-center rounded-lg bg-secondary px-2.5 text-sm font-medium text-secondary-foreground hover:bg-secondary/80"
            >
              Back to login
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-3">
      <Loader2 className="size-6 animate-spin text-muted-foreground" />
      <p className="text-sm text-muted-foreground">
        Signing you into {APP_NAME}…
      </p>
    </div>
  );
}
